import JobsApiRequest from '@/api/jobs'
import {
  JobActivitiesExpandablePropertyType,
  JobExpandablePropertyType,
  JobsFilterType,
  JobType
} from '@/enum'
import {
  CustomFilterTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import NotificationType, { NotificationDuration } from '@/enum/NotificationType'
import { WorkerToMainMessageType } from '@/enum/SignalRWorkerMessageType'
import StatusCodeType from '@/enum/StatusCodeType'
import type { IExplicitlyUpdatedJobProperty, IJob } from '@/interfaces'
import JobRequestMapper from '@/mappers/JobRequestMapper'
import {
  CalendarEvent,
  InspectionForm,
  IssueJobForm,
  Job,
  JobForm,
  JobScheduleOptions,
  JobUpdate,
  ServiceForm,
  WorkOrderJobForm
} from '@/models'
import Activity from '@/models/Activity'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import useInspectionsStore from '@/stores/modules/inspections'
import useServicesStore from '@/stores/modules/services'
import useWorkOrdersStore from '@/stores/modules/work-orders'
import {
  JOB_DETAILS_EXPAND_PARAMS,
  JOB_DETAILS_URL_NAME,
  JOB_EDIT_URL_NAME,
  JOBS_LIST_STORE,
  JOBS_URL_NAME,
  MESSAGE_CONSTANTS,
  UNSCHEDULED_JOBS_LIST_EXPAND_PARAMS
} from '@/utils/constants'
import CONSTANTS from '@/utils/constants/string-constants'
import {
  fetchUntilExhaustion,
  generateFilterExpressionForCalendarEvents,
  getErrorMessageBasedOnRouteURL
} from '@/utils/helpers'
import type { AxiosResponse } from 'axios'
import { defineStore } from 'pinia'
import useGlobalStore from '../global'
import useIssuesStore from '../issues'
import usePaginationStore from '../pagination'
import getters from './getters'
import state from './state'
import ErrorType from '@/enum/ErrorType'
import { MillOrderJobForm } from '@/models/jobs/mill-orders'

const useJobsStore = defineStore('jobsStore', {
  state: () => state,
  getters,
  actions: {
    async getJobs(
      storeName: string,
      responseOptions?: Record<string, string | Record<string, string | string[] | any>>
    ) {
      const jobsListStore = usePaginationStore(storeName)
      const jobApi = new JobsApiRequest()

      const expandableResources = [
        JobExpandablePropertyType.SCHEDULE,
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES,
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE,
        JobExpandablePropertyType.TASK_SHEETS
      ]

      jobsListStore.handleListAPICall<Job>(jobApi.getJobs.bind(jobApi), Job, {
        [ListResponseTypeExpression.Expand]: expandableResources.join(','),
        ...responseOptions
      })
    },
    async getJobsForCalendar(start: string, end: string, options?: any) {
      const jobsApi = new JobsApiRequest()
      const responseOptions = generateFilterExpressionForCalendarEvents(
        JobType.GENERAL,
        start,
        end,
        options
      )
      const result = await fetchUntilExhaustion(jobsApi.getJobs.bind(jobsApi), responseOptions)
      return result.filter(
        (job) => job.scheduledAt || job.dueAt || job.startedAt || job.completedAt
      )
    },
    async getGeneralUnscheduledJobs(storeName: string) {
      const unscheduleJobsListStore = usePaginationStore(storeName)
      const jobsApi = new JobsApiRequest()

      const responseOptions = {
        [ListResponseTypeExpression.Expand]: UNSCHEDULED_JOBS_LIST_EXPAND_PARAMS,
        [ListFilterTypeExpression.Filter]: {
          [CustomFilterTypeExpression.Expression]: `(${JobsFilterType.SCHEDULED_DATE}==null && ${JobsFilterType.START_DATE}==null)`,
          [JobsFilterType.TYPE]: JobType.GENERAL
        }
      }

      unscheduleJobsListStore.loading = true

      try {
        await unscheduleJobsListStore.handleListAPICall<CalendarEvent>(
          jobsApi.getJobs.bind(jobsApi),
          CalendarEvent,
          responseOptions
        )
        return true
      } catch (error) {
        return false
      } finally {
        unscheduleJobsListStore.loading = false
      }
    },
    async createJob(): Promise<AxiosResponse> {
      this.isLoading = true
      const jobsApi = new JobsApiRequest()
      const jobMapper = new JobRequestMapper()
      const newJob = jobMapper.toJobUpdate({ ...this.jobForm, type: JobType.GENERAL })
      const result = await jobsApi.createJob(newJob?.jobRequestData)
      const globalStore = useGlobalStore()

      try {
        if (result.status === StatusCodeType.Success && result.data) {
          // Do something on success here
          globalStore.addToastNotification({
            id: Symbol('Job added'),
            message: `${result.data?.title} added successfully`,
            type: NotificationType.Success
          })
        }
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      } finally {
        this.isLoading = false
      }

      return result
    },
    async updateJob(id: string, explicitlyUpdatedJobProperty?: IExplicitlyUpdatedJobProperty) {
      const jobsApi = new JobsApiRequest(false, true)
      const updatedJob = new JobUpdate({ ...(explicitlyUpdatedJobProperty ?? this.jobForm) })
      try {
        const result = await jobsApi.updateJob(
          {
            id,
            [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
          },
          updatedJob
        )
        return result
      } catch (error: any) {
        let title = error?.response.data?.detail ?? MESSAGE_CONSTANTS.JOB_WAS_DELETED_ALERT_TITLE
        let errorMessage = ''

        const globalStore = useGlobalStore()
        if (error?.status === StatusCodeType.NotFound) {
          title
        } else if (error?.status !== StatusCodeType.Success) {
          switch (updatedJob.type) {
            case JobType.INSPECTION:
              title = MESSAGE_CONSTANTS.INSPECTION_WAS_DELETED_ALERT_TITLE
              errorMessage = MESSAGE_CONSTANTS.INSPECTION_WAS_DELETED_ALERT_MESSAGE
              break
            case JobType.ISSUE:
              title = MESSAGE_CONSTANTS.ISSUE_WAS_DELETED_ALERT_TITLE
              errorMessage = MESSAGE_CONSTANTS.ISSUE_WAS_DELETED_ALERT_MESSAGE
              break
            case JobType.MAINTENANCE:
              title = MESSAGE_CONSTANTS.SERVICE_WAS_DELETED_ALERT_TITLE
              errorMessage = MESSAGE_CONSTANTS.SERVICE_WAS_DELETED_ALERT_MESSAGE
              break
            case JobType.WORK_ORDER:
              title = MESSAGE_CONSTANTS.PURCHASE_ORDER_WAS_DELETED_ALERT_TITLE
              errorMessage = MESSAGE_CONSTANTS.PURCHASE_ORDER_WAS_DELETED_ALERT_MESSAGE
              break
            case JobType.GENERAL:
            default:
              title = MESSAGE_CONSTANTS.JOB_WAS_DELETED_ALERT_TITLE
              errorMessage = MESSAGE_CONSTANTS.JOB_WAS_DELETED_ALERT_MESSAGE
              break
          }
        }
        globalStore.addError(
          {
            name: errorMessage,
            title
          },
          ErrorType.Network
        )
        return error
      }
    },

    async initializeJobDetailsForm(jobData: any, navigator?: any) {
      const currentRouteName = navigator?.getCurrentRoute()?.name

      if (!navigator || currentRouteName == JOB_DETAILS_URL_NAME) {
        switch (jobData.type) {
          case JobType.GENERAL:
            this.jobDetails = new JobForm(jobData)
            break
          case JobType.INSPECTION:
            this.jobDetails = new InspectionForm(jobData)
            break
          case JobType.ISSUE:
            this.jobDetails = new IssueJobForm(jobData)
            break
          case JobType.MAINTENANCE:
            this.jobDetails = new ServiceForm(jobData)
            break
          case JobType.WORK_ORDER:
            this.jobDetails = new WorkOrderJobForm(jobData)
            break
          case JobType.MILL_ORDER:
            this.jobDetails = new MillOrderJobForm(jobData)
            break;
        }
      }

      if (navigator && currentRouteName == JOB_EDIT_URL_NAME) {
        switch (jobData.type) {
          case JobType.GENERAL: {
            this.getJobDetailsAndPopulateJobForm(jobData.id)
            break
          }
          case JobType.INSPECTION: {
            const inspectionsStore = useInspectionsStore()
            inspectionsStore.getInspection(jobData.id)
            break
          }
          case JobType.ISSUE: {
            const issueStore = useIssuesStore()
            issueStore.getIssue(jobData.id)
            break
          }
          case JobType.MAINTENANCE: {
            const servicesStore = useServicesStore()
            servicesStore.getService(jobData.id)
            break
          }
          case JobType.WORK_ORDER: {
            const workOrdersStore = useWorkOrdersStore()
            workOrdersStore.getWorkOrder(jobData.id)
            break
          }
        }
      }
    },

    async getJobDetails(
      jobId: string,
      responseOptions?: Record<string, string>
    ): Promise<AxiosResponse | undefined> {
      const globalStore = useGlobalStore()
      try {
        this.isLoading = true
        const jobsApi = new JobsApiRequest(false, true)

        const options = {
          [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS,
          ...responseOptions
        }
        const result = await jobsApi.getJob({
          id: jobId,
          ...options
        })

        if (result.status === StatusCodeType.Success && result.data) {
          this.initializeJobDetailsForm(result.data)
          return result
        }
      } catch (error: any) {
        const title = error?.response.data?.detail
        const errorMessage = getErrorMessageBasedOnRouteURL()
        globalStore.addError(
          {
            title
          },
          ErrorType.Network
        )
        return error
      } finally {
        this.isLoading = false
      }
    },
    async getJobDetailsAndPopulateJobForm(id: string) {
      this.isLoading = true
      try {
        const expandableResources = [
          JobExpandablePropertyType.SCHEDULE,
          JobExpandablePropertyType.TASK_SHEETS,
          JobExpandablePropertyType.ASSIGNED_EMPLOYEES,
          JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE,
          JobExpandablePropertyType.DOCUMENTS
        ]

        const jobDetails = await this.getJobDetails(id, {
          [ItemTypeExpression.Expand]: expandableResources.join(',')
        })
        if (jobDetails?.status === StatusCodeType.Success && jobDetails.data) {
          this.jobForm = new JobForm(jobDetails.data)
        }
        return jobDetails
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      } finally {
        this.isLoading = false
      }
    },
    async deleteJobs(storeName: string) {
      const jobsListStore = usePaginationStore(storeName)
      try {
        const jobsApi = new JobsApiRequest()

        if (!jobsListStore.selectedItems) throw new Error('No jobs selected for deletion')
        const result = await jobsApi.deleteJobs({
          [DeleteTypeExpression.Expression]: { Id: jobsListStore.selectedItems }
        })
        return result
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      }
    },
    async getJobsNotifications(storeName: string) {
      const jobsListStore = usePaginationStore(storeName)
      const jobsApi = new JobsApiRequest()
      const options = {
        [ListResponseTypeExpression.Expand]: `
        ${JobActivitiesExpandablePropertyType.JOB},
        ${JobActivitiesExpandablePropertyType.USER}
        `
      }
      jobsListStore.handleListAPICall<Activity>(
        jobsApi.getJobNotifications.bind(jobsApi),
        Activity,
        options
      )
    },

    async handleNewJobNotification(jobUpdate: any) {
      const isNewJob = jobUpdate?.job?.createdAt === jobUpdate?.job?.updatedAt
      const isJobAssignedToLoggedInUser = jobUpdate?.job?.assignedEmployees?.some(
        (e: any) => e.employeeId === localStorage.getItem('userId')
      )
      const isJobCreatedByLoggedInUser = localStorage.getItem('userId') == jobUpdate?.job?.updaterId

      if (isNewJob && isJobAssignedToLoggedInUser && !isJobCreatedByLoggedInUser) {
        const globalStore = useGlobalStore()
        globalStore.addToastNotification({
          id: Symbol('NEW_JOB_ASSIGNED'),
          message: jobUpdate.description,
          type: NotificationType.Success,
          timeout: NotificationDuration.Medium,
          callback: () => {
            globalStore.toggleNotificationsFlyout()
            globalStore.removeAllNotifications()
            globalStore.hasNewNotifications = false
          }
        })
      }
    },

    async handleRealtimeJobUpdateOnList(job: IJob) {
      const isNewlyAddedJob = job.updatedAt === job.createdAt
      const isUpdatedJob = !isNewlyAddedJob

      const jobsListStore = usePaginationStore(JOBS_LIST_STORE)
      const jobIndex = jobsListStore.data?.findIndex((item) => item.id === job.id)

      if (isNewlyAddedJob) {
        const isInList = jobsListStore.data?.find((item) => item.id === job.id)
        if (!isInList) {
          this.getJobs(JOBS_LIST_STORE)
        }
      }

      if (isUpdatedJob && jobsListStore.data && jobIndex !== undefined && jobIndex !== -1) {
        jobsListStore.data[jobIndex] = new Job(job)
      }
    },

    async showJobStatusChangeUpdateNotification(updatedData: any, navigator: any) {
      const globalStore = useGlobalStore()
      this.hasIncomingJobChanges = true

      globalStore.addToastNotification({
        id: Symbol(CONSTANTS.JOB_STATUS_CHANGED),
        message: CONSTANTS.JOB_STATUS_CHANGED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Infinite,
        callback: () => {
          globalStore.removeAllNotifications()
          this.initializeJobDetailsForm(updatedData, navigator)
          this.hasIncomingJobChanges = false
        }
      })
    },

    async showJobDetailsChangeUpdateNotification(updatedData: any, navigator: any) {
      const globalStore = useGlobalStore()
      this.hasIncomingJobChanges = true

      globalStore.addToastNotification({
        id: Symbol(CONSTANTS.JOB_UPDATED),
        message: CONSTANTS.JOB_UPDATED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Infinite,
        callback: () => {
          globalStore.removeAllNotifications()
          this.initializeJobDetailsForm(updatedData, navigator)
          this.hasIncomingJobChanges = false
        }
      })
    },

    async handleRealtimeJobActivityUpdate(updatedData: any, navigator: any) {
      this.handleNewJobNotification(updatedData)
    },

    async handleJobRealtimeDetailsUpdate(updatedData: any, navigator: any) {
      this.hasIncomingJobChanges = false

      const currentRouteName = navigator.getCurrentRoute()?.name
      const loggedInUserId = localStorage.getItem('userId')
      const isUserOnJobDetailsView =
        currentRouteName == JOB_DETAILS_URL_NAME &&
        updatedData.id == navigator.getCurrentRoute()?.params?.id
      const isUserOnJobEditView =
        currentRouteName == JOB_EDIT_URL_NAME &&
        updatedData.id == navigator.getCurrentRoute()?.params?.id
      const hasJobDetailsStatusChanged = this.jobDetails.status !== updatedData.status
      const hasJobEditDetailsStatusChanged = this.jobForm.status !== updatedData.status
      const hasLoggedInUserUpdatedTheJob = loggedInUserId == updatedData.updaterId

      // handle job update on job details view
      if (isUserOnJobDetailsView) {
        if (hasJobDetailsStatusChanged && hasLoggedInUserUpdatedTheJob) {
          return
        }

        if (hasJobDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobStatusChangeUpdateNotification(updatedData, navigator)
        }

        if (!hasJobDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          const jobsApi = new JobsApiRequest()
          const updatedJob = await jobsApi.getJob({
            id: updatedData.id,
            [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
          })
          this.showJobDetailsChangeUpdateNotification(updatedJob?.data ?? updatedData, navigator)
        }
      }

      // handle job update on job edit view
      if (isUserOnJobEditView) {
        if (hasJobEditDetailsStatusChanged && hasLoggedInUserUpdatedTheJob) {
          return
        }

        if (hasJobEditDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobStatusChangeUpdateNotification(updatedData, navigator)
        }

        if (!hasLoggedInUserUpdatedTheJob) {
          this.showJobDetailsChangeUpdateNotification(updatedData, navigator)
        }
      }
    },

    async handleJobRealtimeUpdate(updateType: any, updatedData: any, navigator: any) {
      const globalStore = useGlobalStore()
      const currentRouteName = navigator.getCurrentRoute()?.name
      const isUserOnJobsListView = currentRouteName == JOBS_URL_NAME

      const isJobUpdate = updateType == WorkerToMainMessageType.UpdateJob
      const isJobActivityUpdate = updateType == WorkerToMainMessageType.UpdateJobActivity
      const isJobDetailsUpdate = updateType == WorkerToMainMessageType.UpdateJobDetails

      if (isJobActivityUpdate || isJobUpdate) {
        globalStore.hasNewNotifications = true
      }

      if (isJobActivityUpdate) {
        this.handleRealtimeJobActivityUpdate(updatedData, navigator)
      }

      if (isJobDetailsUpdate) {
        if (isUserOnJobsListView) {
          this.handleRealtimeJobUpdateOnList(updatedData)
        } else {
          this.handleJobRealtimeDetailsUpdate(updatedData, navigator)
        }
      }
    },

    async getUnscheduledJobs(storeName: string) {
      const unscheduleJobsListStore = usePaginationStore(storeName)
      const jobsApi = new JobsApiRequest()
      const expandableResources = [
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES,
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE,
        JobExpandablePropertyType.SCHEDULE
      ]

      const responseOptions = {
        [ListResponseTypeExpression.Expand]: expandableResources.join(','),
        [ListFilterTypeExpression.Filter]: {
          [CustomFilterTypeExpression.Expression]: `(${JobsFilterType.SCHEDULED_DATE}==null && ${JobsFilterType.START_DATE}==null)`
        }
      }

      unscheduleJobsListStore.handleListAPICall<CalendarEvent>(
        jobsApi.getJobs.bind(jobsApi),
        CalendarEvent,
        responseOptions
      )
    },

    resetJobForm() {
      this.jobForm = new JobForm()
      this.jobDetails = new JobForm()
      this.hasIncomingJobChanges = false
    },
    resetRecurringJobSchedule() {
      this.jobForm.schedule = new JobScheduleOptions()
      this.hasIncomingJobChanges = false
    }
  }
})
export default useJobsStore
